import i18nFactory from '@yandex-int/i18n';
import { Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { Action } from '../../../actions';
import { onAcceptBulkImageReplacement } from '../../../actions/banners-edit/banner-text-image';
import {
    acceptMassActionsCallouts,
    acceptMassActionsLinkDisplayedBulkReplacementEditor,
    acceptMassActionsSiteLinksEditor,
    changeMassActionsCalloutsRadioState,
    changeMassActionsSiteLinksRadio,
    changeMassActionsTextBulkRadio,
    ON_ACCEPT_MASS_ACTIONS_CALLOUTS,
    ON_ACCEPT_MASS_ACTIONS_IMAGE,
    ON_ACCEPT_MASS_ACTIONS_LINK_BULK_PATH_REPLACEMENT_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_LINK_BULK_REPLACEMENT_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_LINK_DISPLAYED_BULK_REPLACEMENT_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_SITE_LINKS_EDITOR,
    ON_ACCEPT_MASS_ACTIONS_VIDEO,
    ON_SAVE_MASS_ACTIONS_TEXT_BULK_REPLACEMENT_EDITOR,
    OnAcceptMassActionsImageAction,
    OnAcceptMassActionsLinkBulkPathReplacementEditorAction,
    OnAcceptMassActionsLinkBulkReplacementEditorAction,
    OnAcceptMassActionsSiteLinksEditorAction,
    OnAcceptMassActionsVideoAction,
    onAcceptMassActionsVideoBulkReplacementEditor,
    onMassActionsBulkImageReplacementEditorToggleChanged,
    onMassActionsLinkDisplayedBulkReplacementEditorRadioButtonStateChanged,
    onMassActionsVideoBulkReplacementEditorRadioButtonStateChanged,
    OnSaveMassActionsTextBulkReplacementEditorAction,
    saveMassActionsLinkBulkPathReplacementEditor,
    saveMassActionsLinkBulkReplacementEditor,
    saveMassActionsLinkCreativeBulkReplacementEditor,
    saveMassActionsTextBulkReplacementEditor,
    showErrorMassActionsErrorEditor
} from '../../../actions/banners-edit/banners-edit-mass-actions';
import { BulkReplacementRadio } from '../../../components/BulkReplacementModeChooser/BulkReplacementModeChooser';
import { getEditBannerHrefValidateText, HrefValidationStatuses } from '../../../service/BannerHelpers/BannerHelpers';
import {
    isBannerEditMassActionsAvailable,
    replaceUrlPart,
    UrlPart
} from '../../../service/BannersEditMassActionsHelpers/BannersEditMassActionsHelpers';
import { EpicDeps } from '../../../service/StoreFactory';
import { BannerId } from '../../../typings/banners';
import { MassActionId } from '../../../typings/bannersEditState';
import { State } from '../../../typings/state';

import * as keyset from './bannersEditMassAction.i18n';

const i18n = i18nFactory(keyset);

export const onAcceptMassActionsImageEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ON_ACCEPT_MASS_ACTIONS_IMAGE),
        map(
            (action: OnAcceptMassActionsImageAction): Action => {
                const data = store$.value.imageEditor[MassActionId.bannersEdit].image.data;
                const { replaceOrDeleteToggle } = store$.value.bannersEditMassActions[MassActionId.bannersEdit].data;

                if (data === undefined && replaceOrDeleteToggle === BulkReplacementRadio.REPLACE) {
                    return onMassActionsBulkImageReplacementEditorToggleChanged(
                        MassActionId.bannersEdit,
                        BulkReplacementRadio.DELETE
                    );
                } else {
                    return onAcceptBulkImageReplacement();
                }
            }
        )
    );
};

export const onAcceptMassActionsVideoEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ON_ACCEPT_MASS_ACTIONS_VIDEO),
        map(
            (action: OnAcceptMassActionsVideoAction): Action => {
                const data = (store$.value.video[action.massActionId] || {}).data;
                const { currentActiveRadioButtonVideo } = store$.value.bannersEditMassActions[action.massActionId].data;

                if (data === undefined && currentActiveRadioButtonVideo === BulkReplacementRadio.REPLACE) {
                    return onMassActionsVideoBulkReplacementEditorRadioButtonStateChanged(
                        action.massActionId,
                        BulkReplacementRadio.DELETE
                    );
                } else {
                    return onAcceptMassActionsVideoBulkReplacementEditor(action.massActionId);
                }
            }
        )
    );
};

export const onAcceptMassActionsLinkDisplayedBulkReplacementEditorEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ON_ACCEPT_MASS_ACTIONS_LINK_DISPLAYED_BULK_REPLACEMENT_EDITOR),
        map(
            (action: OnSaveMassActionsTextBulkReplacementEditorAction): Action => {
                const { currentActiveRadioButtonDisplayedLinks, newVisibleLink } = store$.value.bannersEditMassActions[
                    action.massActionId
                ].data;

                if (!newVisibleLink && currentActiveRadioButtonDisplayedLinks === BulkReplacementRadio.REPLACE) {
                    return onMassActionsLinkDisplayedBulkReplacementEditorRadioButtonStateChanged(
                        action.massActionId,
                        BulkReplacementRadio.DELETE
                    );
                } else {
                    return acceptMassActionsLinkDisplayedBulkReplacementEditor(action.massActionId);
                }
            }
        )
    );
};

export const onSaveMassActionsTextBulkReplacementEditorEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ON_SAVE_MASS_ACTIONS_TEXT_BULK_REPLACEMENT_EDITOR),
        map(
            (action: OnSaveMassActionsTextBulkReplacementEditorAction): Action => {
                const { currentTextBulkRadioState, newText } = store$.value.bannersEditMassActions[
                    action.massActionId
                ].data;

                if (!newText && currentTextBulkRadioState === BulkReplacementRadio.REPLACE) {
                    return changeMassActionsTextBulkRadio(action.massActionId, BulkReplacementRadio.DELETE);
                } else {
                    return saveMassActionsTextBulkReplacementEditor(action.massActionId);
                }
            }
        )
    );
};

export const onAcceptMassActionsCalloutsEditorEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ON_ACCEPT_MASS_ACTIONS_CALLOUTS),
        map(
            (action: OnAcceptMassActionsLinkBulkReplacementEditorAction): Action => {
                const { selected } = store$.value.calloutsEditor.data[action.massActionId];
                const { currentCalloutsRadioState } = store$.value.bannersEditMassActions[action.massActionId].data;

                if (selected.length === 0 && currentCalloutsRadioState === BulkReplacementRadio.REPLACE) {
                    return changeMassActionsCalloutsRadioState(action.massActionId, BulkReplacementRadio.DELETE);
                } else {
                    return acceptMassActionsCallouts(action.massActionId);
                }
            }
        )
    );
};

export const onAcceptMassActionsLinkBulkReplacementEditorEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ON_ACCEPT_MASS_ACTIONS_LINK_BULK_REPLACEMENT_EDITOR),
        mergeMap(
            (action: OnAcceptMassActionsLinkBulkReplacementEditorAction): Observable<Action> => {
                const { commonData, bannersEditMassActions } = store$.value;
                const { newVisibleLink, selectedProtocolValue, isSiteLinksTumblerChecked } = bannersEditMassActions[
                    action.massActionId
                ].data;

                const { login } = commonData;
                const url = selectedProtocolValue + newVisibleLink;

                return from(deps.restApiManager.checkMassUrl(login, [url])).pipe(
                    mergeMap(
                        ({ check_results = [], validation_errors = [] }): Observable<Action> => {
                            const hrefValidationError =
                                (check_results[0] && check_results[0].error) ||
                                (validation_errors[0] && validation_errors[0].code);

                            if (hrefValidationError) {
                                return of(
                                    showErrorMassActionsErrorEditor(
                                        [
                                            {
                                                path: 'link-replacement-input',
                                                text: getEditBannerHrefValidateText(
                                                    HrefValidationStatuses[hrefValidationError]
                                                )
                                            }
                                        ],
                                        MassActionId.bannersEdit
                                    )
                                );
                            }

                            if (action.isCreative) {
                                return of(saveMassActionsLinkCreativeBulkReplacementEditor(MassActionId.bannersEdit));
                            }

                            return of(
                                saveMassActionsLinkBulkReplacementEditor(
                                    MassActionId.bannersEdit,
                                    isSiteLinksTumblerChecked,
                                    check_results[0].domain
                                )
                            );
                        }
                    ),
                    catchError(
                        (): Observable<Action> =>
                            of(
                                showErrorMassActionsErrorEditor(
                                    [
                                        {
                                            path: '',
                                            text: i18n('unhandled-error')
                                        }
                                    ],
                                    MassActionId.bannersEdit
                                )
                            )
                    )
                );
            }
        )
    );
};

export const onAcceptMassActionsLinkBulkPathReplacementEditorEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) => {
    return action$.pipe(
        ofType(ON_ACCEPT_MASS_ACTIONS_LINK_BULK_PATH_REPLACEMENT_EDITOR),
        mergeMap(
            (action: OnAcceptMassActionsLinkBulkPathReplacementEditorAction): Observable<Action> => {
                const {
                    commonData: { login },
                    editBanners: { banners },
                    bannersEditMassActions
                } = store$.value;
                const {
                    newPartUrl,
                    targetPartUrl,
                    isParametersTumblerChecked,
                    isDomainAndPathTumblerChecked,
                    selectedProtocolValue,
                    isSiteLinksTumblerChecked
                } = bannersEditMassActions[MassActionId.bannersEdit].data;

                if (!isDomainAndPathTumblerChecked) {
                    return of(
                        saveMassActionsLinkBulkPathReplacementEditor(action.massActionId, isSiteLinksTumblerChecked)
                    );
                }

                const urls: Array<string> = Object.keys(banners).reduce((urlsArray: Array<string>, next: BannerId) => {
                    const currentBanner = {
                        ...banners[next],
                        errors: undefined
                    };
                    const urlPart: UrlPart[] = [];

                    if (isDomainAndPathTumblerChecked) {
                        urlPart.push(UrlPart.DOMAIN_AND_PATH);
                    }

                    if (isParametersTumblerChecked) {
                        urlPart.push(UrlPart.PARAMS);
                    }

                    if (isBannerEditMassActionsAvailable(currentBanner)) {
                        const { contacts } = currentBanner;
                        const { href } = contacts;

                        urlsArray.push(
                            selectedProtocolValue +
                                replaceUrlPart(href, {
                                    from: targetPartUrl,
                                    to: newPartUrl,
                                    urlPart: urlPart
                                })
                        );
                    }

                    return urlsArray;
                }, []);

                return from(deps.restApiManager.checkMassUrl(login, urls)).pipe(
                    mergeMap(
                        ({ check_results = [] }): Observable<Action> => {
                            let resultIndex = 0;
                            const domains = Object.keys(banners).reduce((acc, next: BannerId) => {
                                const currentBanner = {
                                    ...banners[next],
                                    errors: undefined
                                };

                                if (isBannerEditMassActionsAvailable(currentBanner)) {
                                    acc[next] = check_results[resultIndex] ? check_results[resultIndex].domain : '';
                                    resultIndex++;
                                }

                                return acc;
                            }, {});

                            return of(
                                saveMassActionsLinkBulkPathReplacementEditor(
                                    action.massActionId,
                                    isSiteLinksTumblerChecked,
                                    domains
                                )
                            );
                        }
                    ),
                    catchError(() =>
                        of(
                            saveMassActionsLinkBulkPathReplacementEditor(
                                action.massActionId,
                                isSiteLinksTumblerChecked,
                                {}
                            )
                        )
                    )
                );
            }
        )
    );
};

export const onAcceptMassActionSiteLinksDataEpic: Epic<Action, Action, State, EpicDeps> = (action$, store$, deps) => {
    return action$.pipe(
        ofType(ON_ACCEPT_MASS_ACTIONS_SITE_LINKS_EDITOR),
        map(
            (action: OnAcceptMassActionsSiteLinksEditorAction): Action => {
                const { items } = store$.value.siteLinksEditor.data[action.massActionId];
                const { currentSiteLinksActiveRadioButton } = store$.value.bannersEditMassActions[
                    action.massActionId
                ].data;
                const isSiteLinksEmpty = items.every(item => {
                    return (
                        item.title === '' &&
                        item.isMassActionsTitleDifferent === undefined &&
                        item.link === '' &&
                        item.isMassActionsLinkDifferent === undefined &&
                        item.turbo === undefined &&
                        item.isMassActionsTurboDifferent === undefined &&
                        item.descr === '' &&
                        item.isMassActionsDescrDifferent === undefined
                    );
                });

                if (isSiteLinksEmpty && currentSiteLinksActiveRadioButton === BulkReplacementRadio.REPLACE) {
                    return changeMassActionsSiteLinksRadio(action.massActionId, BulkReplacementRadio.DELETE);
                } else {
                    return acceptMassActionsSiteLinksEditor(action.massActionId);
                }
            }
        )
    );
};
